import { useEffect, type JSX, type RefObject } from 'react';
import { resolveRenderTier, type RenderTierPref } from '../../lib/device-tier';
import { useRenderSettingsStore } from '../../state/render-settings.store';
import type { OfficeEngine } from './render/office-engine';

/**
 * Per-device RENDER QUALITY — a manual override for the office resolution tier (auto / full / lite) and the
 * glue that swaps the live renderer the instant it changes. Two exports:
 *   • {@link useApplyRenderSettings} — a hook (mounted once in OfficePage) that reflects the store onto the
 *     engine via {@link OfficeEngine.applyRenderTier} (a live `renderer.resize`, no reload).
 *   • {@link RenderQualityControl} — the segmented control shown in the HUD "Me" menu, beside the audio
 *     faders.
 *
 * LOCAL & per-device, never shared (Constitution Art. 1) — a comfort knob, not a KPI. The auto-detect
 * (lib/device-tier) stays the default; this just lets a player force the trade-off either way.
 */

// ── Apply: store → engine ──────────────────────────────────────────────────────────────────────────

export function useApplyRenderSettings(engineRef: RefObject<OfficeEngine | null>): void {
  const tier = useRenderSettingsStore((s) => s.tier);
  // Re-apply whenever the preference changes. Also runs once on mount, which harmlessly re-asserts the
  // resolution the engine already chose from the same store value at init (applyRenderTier no-ops if equal).
  useEffect(() => {
    engineRef.current?.applyRenderTier(resolveRenderTier(tier));
  }, [tier, engineRef]);
}

// ── Panel: the segmented control ─────────────────────────────────────────────────────────────────

interface TierOption {
  value: RenderTierPref;
  label: string;
  hint: string;
}

const OPTIONS: readonly TierOption[] = [
  { value: 'auto', label: 'Auto', hint: 'Matches your device' },
  { value: 'full', label: 'Full', hint: 'Crispest — more GPU' },
  { value: 'lite', label: 'Lite', hint: 'Smoothest — lighter' },
];

/**
 * The "Picture quality" control for the Me menu: a 3-way Auto / Full / Lite selector over the render
 * resolution tier, applied live. Auto is the default and right for almost everyone; Full/Lite let a player
 * trade sharpness against frame rate on their own device.
 */
export function RenderQualityControl(): JSX.Element {
  const tier = useRenderSettingsStore((s) => s.tier);
  const setTier = useRenderSettingsStore((s) => s.setTier);
  const activeHint = OPTIONS.find((o) => o.value === tier)?.hint ?? '';

  return (
    <div className="flex flex-col gap-1.5" role="group" aria-label="Picture quality">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-white/80">🖥️ Picture quality</span>
        <span aria-hidden="true" className="shrink-0 text-[10px] text-white/40">
          {activeHint}
        </span>
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label="Picture quality">
        {OPTIONS.map((o) => {
          const selected = o.value === tier;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setTier(o.value)}
              title={o.hint}
              className={`flex-1 rounded-lg px-2 py-1 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                selected
                  ? 'bg-amber-400 text-slate-900'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <p className="px-1 text-[10px] leading-snug text-white/40">
        Lite lowers resolution for smoother frames on a busy or older device — applies instantly.
      </p>
    </div>
  );
}
